import {
  WSActions,
  WSControlGroup,
  WSElement,
  WSInfoBox,
  WSList,
  WSPanel,
  WSText
} from "@wingspanhq/fe-component-library";
import { OnboardingStep } from "../../../../Onboarding/types";
import React from "react";
import { useCreatePlanEnrollment } from "../../../../../query/benefits";
import { useUserId } from "../../../../../query/hooks/helpers";
import { WorkClassification } from "../../../../../services/api/planEnrollment/types";
import { WorkClassificationToNCCI } from "../../../../../services/api/planEnrollment/constants";
import { WSErrorMessage } from "../../../../../components/WSErrorMessage/WSErrorMessage";
import { OAI_KEY, PLAN_DETAILS } from "../../../utils/constants";
import { WS_LINKS } from "../../../../../types/wsLinks";

const { monthlyPremiumFormatted, name, monthlyPremium } = PLAN_DETAILS[OAI_KEY];

export const StepCertify: React.FC<OnboardingStep> = props => {
  const userId = useUserId();
  const [createEnrollment, createEnrollmentMeta] = useCreatePlanEnrollment();

  const [workClassification, setWorkClassification] =
    React.useState<string>("");
  const [attestations, setAttestations] = React.useState({});

  const assertationsLabels: React.ReactNode[] = [
    <WSText.ParagraphSm color="gray600">
      <b>Coverage acknowledgement:</b> I acknowledge that this is Occupational
      Accident Insurance and it is NOT Workers' Compensation insurance.
    </WSText.ParagraphSm>,
    <WSText.ParagraphSm color="gray600">
      <b>Work type confirmation:</b> I attest that I am an eligible independent
      contractor and the work I perform is consistent with the types of work
      covered under the Wingspan Occupational Accident Insurance Master Policy.
    </WSText.ParagraphSm>,
    <WSText.ParagraphSm color="gray600">
      <b>Consent to payment terms:</b> I understand and agree that a premium of
      {monthlyPremiumFormatted} of my non-reimbursable earnings will be
      automatically deducted from payments I receive through Wingspan to
      maintain my coverage.
    </WSText.ParagraphSm>,
    <WSText.ParagraphSm color="gray600">
      <b>ODICA membership:</b> I understand that enrolling in this policy
      automatically enrolls me as a member of the On-Demand Independent.
      Contractors Association (ODICA).
    </WSText.ParagraphSm>,
    <WSText.ParagraphSm color="gray600">
      <b>Policy review:</b> I have reviewed and accept the terms and conditions
      of the Master Policy.{" "}
      <a href={WS_LINKS.wingspanAOIPolicy} target="_blank" rel="noreferrer">
        View policy document.
      </a>
    </WSText.ParagraphSm>
  ];

  const assertationOptions = assertationsLabels.map((label, index) => ({
    label,
    value: `${index + 1}`
  }));

  const primaryOccupation =
    WorkClassificationToNCCI[workClassification as WorkClassification];

  const isReadyToSubmit =
    !!primaryOccupation &&
    Object.values(attestations).filter(Boolean).length ===
      assertationsLabels.length;

  return (
    <WSPanel>
      <WSList gap="2XL">
        <WSElement>
          <WSText.Paragraph weight="medium" mb="XL">
            Eligibility & certification
          </WSText.Paragraph>
          <WSText.ParagraphSm color="gray500" mb="L">
            Please review the eligibility requirements and certify the following
            statements to enroll.
          </WSText.ParagraphSm>
          <WSList gap="S" mb="L">
            <WSInfoBox title="Eligibility requirements" icon="user-check">
              <ul>
                <li>Be at least eighteen (18) years of age.</li>
                <li>
                  You are under written contract to provide services facilitated
                  by Wingspan.
                </li>
                <li>
                  You are classified and treated by your client as an
                  Independent Contractor (1099), not an employee
                </li>
              </ul>
            </WSInfoBox>

            <WSInfoBox icon="briefcase" title="Your work classification:">
              Please select the category that best describes the primary work
              you will be performing. This selection determines your premium
              rate.
              <WSElement pt="L" />
              <WSControlGroup
                type="radio"
                value={workClassification}
                name="workClassification"
                onChange={setWorkClassification}
                chip={true}
                size="S"
                options={[
                  {
                    value: WorkClassification.Driving,
                    label: (
                      <>
                        <b>Driving & Delivery:</b> Operating a motor vehicle to
                        transport goods, materials, or people.
                      </>
                    )
                  },
                  {
                    value: WorkClassification.AutoDetailing,
                    label: (
                      <>
                        <b>Auto Detailing:</b> Performing cleaning, washing, or
                        restoration services on the interior or exterior of
                        vehicles.
                      </>
                    )
                  },
                  {
                    value: WorkClassification.ClaimsInspector,
                    label: (
                      <>
                        <b>Claims Inspector:</b> Inspecting and assessing damage
                        to property or vehicles to report on a claim.
                      </>
                    )
                  },
                  {
                    value: WorkClassification.Clerical,
                    label: (
                      <>
                        <b>Professional & Administrative Services:</b> Providing
                        technical, creative, administrative, or other
                        professional support services (e.g., development,
                        design, data management, contact center work).
                      </>
                    )
                  }
                ]}
              />
            </WSInfoBox>

            <WSInfoBox icon="accept" title="You agree to:">
              (Checkboxes required for all items below)
              <WSControlGroup
                type="checkbox"
                value={attestations}
                name="attestations"
                onChange={setAttestations}
                size="S"
                mt="M"
                options={assertationOptions}
              />
            </WSInfoBox>
          </WSList>
          <WSText.ParagraphSm color="gray500">
            Wingspan Solutions LLC is not a licensed insurance agency. The
            program is administered by World Trade Labs, Inc. dba Assurely.
            Coverage is underwritten by Atlantic Specialty Insurance Company.
          </WSText.ParagraphSm>
        </WSElement>

        <WSErrorMessage error={createEnrollmentMeta.error} contextKey="Other" />

        <WSActions
          alignment="fill"
          buttons={[
            {
              kind: "Tertiary",
              onClick: props.onBack,
              label: "Back"
            },
            {
              disabled: !isReadyToSubmit,
              label: "Continue",
              loading: createEnrollmentMeta.isLoading,
              type: "submit",
              onAsyncClick: async () => {
                await createEnrollment(
                  {
                    planType: "WingspanOccupationalAccidentInsurance",
                    primaryOccupation
                  },
                  {
                    onSuccess: () => {
                      props.onNext?.();
                    }
                  }
                );
              }
            }
          ]}
        />
      </WSList>
    </WSPanel>
  );
};
